import React, { useCallback, useEffect, useMemo, useState } from 'react';
import DOMPurify from 'dompurify';
import { protectService, accountService, authService, mailboxService, aiService } from '../services/api';
import { useToast } from '../ui/Toast';
import { useConfirm } from '../ui/Confirm';
import { Toggle, EmptyState, ErrorState } from '../ui/primitives';
import { track } from '../lib/analytics';
import { Link } from 'react-router-dom';
import { Settings as SettingsIcon, Shield, X, Mail, Refresh, Google, Search, ChevronRight, Sparkles, Check, Alert } from '../ui/icons';
import Modal from '../ui/Modal';
import { useHotel } from '../ui/hotel/HotelTheme';
import PageHead from '../components/hotel/PageHead';
import Spinner from '../ui/Spinner';

// The API answers with a JSON object on some routes and a bare string on others
// (the protected-senders validation errors are plain text). One reader for both,
// so a real server message is never swallowed in favour of a generic fallback.
function errText(err, fallback) {
  const data = err?.response?.data;
  if (typeof data === 'string' && data.trim()) return data.trim();
  return data?.error || fallback;
}

const DEFAULT_DIGEST_HOUR = 7;
const HOURS = Array.from({ length: 24 }, (_, h) => h);
const pad2 = (n) => String(n).padStart(2, '0');

// Midnight is a legitimate choice. The previous guard (`digestHourUTC > 0`)
// treated 0 as "unset" and silently rewrote it to 7, so picking midnight was
// impossible: the select snapped back on every reload.
function normalizeHour(value) {
  return Number.isInteger(value) && value >= 0 && value <= 23 ? value : DEFAULT_DIGEST_HOUR;
}

// The real instant matching `utcHour` today. Going through an actual Date is
// what makes the conversion correct for daylight saving and for the zones whose
// offset is not a whole number of hours; a hardcoded +1/+2 would not be.
function instantAt(utcHour) {
  const now = new Date();
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), utcHour, 0, 0));
}

function localTimeLabel(utcHour) {
  return instantAt(utcHour).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
}

// Far enough from Greenwich, an early UTC hour lands on the previous day
// locally. Saying only "23:00 chez vous" would let the user expect the digest
// on the wrong day.
function dayShift(utcHour) {
  const d = instantAt(utcHour);
  const diff =
    Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()) -
    Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
  return Math.round(diff / 86400000);
}

function localZoneName() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || '';
  } catch (err) {
    // Older engines can throw here; the hour itself is still shown.
    return '';
  }
}

// Opt in to hands-free background syncing: a scheduler periodically pulls the
// inbox and (when rule autopilot is on) applies the user's deterministic rules,
// with no manual click. Off by default so Mailsorter never touches Gmail
// unprompted.
//
// The settings come from the parent: this card used to fetch them itself, which
// meant three identical GETs per visit.
function AutoSyncSettings({ settings, onSaved }) {
  const toast = useToast();
  const [saving, setSaving] = useState(false);
  const enabled = !!settings.autoSyncEnabled;

  const persist = async (next) => {
    setSaving(true);
    try {
      const { data } = await accountService.updateSettings({ autoSyncEnabled: next });
      onSaved(data);
      toast.success(next ? 'Synchronisation automatique activée.' : 'Synchronisation automatique désactivée.');
    } catch (err) {
      toast.error(errText(err, 'Enregistrement impossible.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card animate-fade-up mt-6 p-7">
      <div className="mb-1 flex items-center gap-2">
        <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-brand-50 text-brand-600">
          <Refresh size={18} />
        </span>
        <h2 className="text-lg font-bold text-ink-900">Synchronisation automatique</h2>
      </div>
      <p className="mb-5 text-sm text-muted">
        Laissez Mailsorter <span className="font-semibold text-ink-700">synchroniser votre boîte en arrière-plan</span>,
        sans aucun clic. Si l'<span className="font-semibold text-ink-700">application automatique des règles</span> est
        activée (page Règles), vos règles déterministes trient aussi vos nouveaux emails toutes seules, le chemin
        mains-libres vers l'Inbox Zero.
      </p>

      <div className="rounded-xl border border-hairline p-4">
        <Toggle
          id="auto-sync"
          checked={enabled}
          disabled={saving}
          onChange={persist}
          label="Synchroniser ma boîte automatiquement"
          description="Mailsorter relève vos nouveaux emails sans que vous ouvriez l'app."
        />
        {saving && (
          <p className="mt-3 flex items-center gap-2 text-xs text-muted">
            <Spinner size={14} className="text-brand-500" /> Enregistrement…
          </p>
        )}
      </div>
    </div>
  );
}

// Opt in to the daily email digest: a once-a-day recap of the last 7 days of
// triage, sent to the user's own inbox at a chosen hour.
//
// The hour travels to the server in UTC (unchanged contract) but is never shown
// alone: the audience is French, i.e. one or two hours ahead depending on the
// season, and "07:00" meant two different things to the user and to the server.
function DigestSettings({ settings, onSaved }) {
  const hotel = useHotel();
  const toast = useToast();
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const enabled = !!settings.digestEnabled;
  const hour = normalizeHour(settings.digestHourUTC);

  const openPreview = async () => {
    setLoadingPreview(true);
    try {
      const { data } = await accountService.getDigestPreview();
      setPreview(data);
    } catch (err) {
      toast.error(errText(err, "L'aperçu n'a pas pu être chargé."));
    } finally {
      setLoadingPreview(false);
    }
  };

  const sendTest = async () => {
    setSendingTest(true);
    try {
      const { data } = await accountService.sendTestDigest();
      toast.success(
        data.total > 0
          ? 'Digest envoyé : regardez votre boîte.'
          : 'Digest envoyé. Votre semaine est vide, le récap le dit tel quel.'
      );
    } catch (err) {
      toast.error(errText(err, "L'envoi de test a échoué."));
    } finally {
      setSendingTest(false);
    }
  };

  // The offset cannot change while the page is open, so the 24 labels are built
  // once instead of on every keystroke elsewhere in the tree.
  const options = useMemo(
    () => HOURS.map((h) => ({ h, label: `${pad2(h)}:00 UTC (${localTimeLabel(h)} chez vous)` })),
    []
  );
  const zone = useMemo(localZoneName, []);
  const shift = dayShift(hour);
  const shiftLabel = shift === 1 ? ' le lendemain' : shift === -1 ? ' la veille' : '';

  const persist = async (next) => {
    setSaving(true);
    try {
      const { data } = await accountService.updateSettings({
        digestEnabled: next.enabled,
        digestHourUTC: next.hour,
      });
      onSaved(data);
      toast.success(next.enabled ? 'Digest quotidien activé.' : 'Réglages du digest enregistrés.');
    } catch (err) {
      toast.error(errText(err, 'Enregistrement impossible.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card animate-fade-up mt-6 p-7">
      <div className="mb-1 flex items-center gap-2">
        <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-brand-50 text-brand-600">
          <Mail size={18} />
        </span>
        <h2 className="text-lg font-bold text-ink-900">Digest quotidien</h2>
      </div>
      <p className="mb-5 text-sm text-muted">
        Recevez chaque jour un <span className="font-semibold text-ink-700">récap de votre tri des 7 derniers jours</span>,
        directement dans votre boîte.
        <span className="mt-1 block text-xs text-muted">
          Astuce : si rien n'arrive, reconnectez Gmail depuis la carte « Compte Gmail » ci-dessous pour réautoriser
          l'envoi.
        </span>
      </p>

      <div className="rounded-xl border border-hairline p-4">
        <Toggle
          id="digest-enabled"
          checked={enabled}
          disabled={saving}
          onChange={(next) => persist({ enabled: next, hour })}
          label="Activer l'envoi quotidien"
          description="Un seul email par jour, envoyé à vous-même."
        />

        <div className="mt-4 border-t border-hairline pt-4">
          <label htmlFor="digest-hour" className="block text-sm font-bold text-ink-900">
            Heure d'envoi
          </label>
          <select
            id="digest-hour"
            className="select mt-2 w-auto max-w-full"
            value={hour}
            disabled={saving || !enabled}
            onChange={(e) => persist({ enabled, hour: parseInt(e.target.value, 10) })}
          >
            {options.map((o) => (
              <option key={o.h} value={o.h}>
                {o.label}
              </option>
            ))}
          </select>
          <p className="mt-2 text-xs text-muted">
            Programmé à {pad2(hour)}:00 UTC, soit {localTimeLabel(hour)}
            {shiftLabel} chez vous{zone ? ` (${zone})` : ''}.
          </p>
        </div>

        {saving && (
          <p className="mt-3 flex items-center gap-2 text-xs text-muted">
            <Spinner size={14} className="text-brand-500" /> Enregistrement…
          </p>
        )}

        {/* Deux questions que le réglage seul ne répondait pas : à quoi
            ressemble ce mail, et l'envoi fonctionne-t-il vraiment ? La seconde
            demandait d'attendre un jour pour découvrir que l'autorisation Gmail
            n'avait plus le scope d'envoi. */}
        <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-hairline pt-4">
          <button onClick={openPreview} disabled={loadingPreview} className="btn-secondary btn-sm">
            {loadingPreview ? <Spinner size={15} /> : <Search size={15} />} Voir un aperçu
          </button>
          <button onClick={sendTest} disabled={sendingTest} className="btn-ghost btn-sm">
            {sendingTest ? <Spinner size={15} /> : <Mail size={15} />} M'envoyer un test
          </button>
          <span className="text-xs text-muted">Un test n'utilise pas le digest du jour.</span>
        </div>
      </div>

      {preview && (
        <Modal
          open
          onClose={() => setPreview(null)}
          title="Aperçu du digest"
          description="Exactement ce que vous recevrez, rendu depuis vos 7 derniers jours."
          size="lg"
        >
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-muted">Objet</p>
            <p className="mb-4 font-bold text-ink-900">{preview.subject}</p>
            <p className="text-xs font-semibold uppercase tracking-wide text-muted">Contenu</p>
            {/* Le HTML vient de notre propre moteur de rendu (internal/digest),
                mais il transporte des données de l'utilisateur : il passe donc
                par dompurify comme tout HTML affiché dans l'app. */}
            {hotel ? (
              <DigestFrame html={DOMPurify.sanitize(preview.html || '')} />
            ) : (
              <div
                className="email-body mt-1 rounded-xl border border-hairline p-4"
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(preview.html || '') }}
              />
            )}
          </div>
        </Modal>
      )}
    </div>
  );
}

// The hotel recap is laid out with tables, the way email clients need it, and
// .email-body turns tables into scrolling blocks (index.css) so a newsletter
// cannot widen the reader. Rendered there, the preview would not be "exactly
// what you will receive". So under the hotel it gets a document of its own:
// an iframe that runs nothing (no allow-scripts) and only lets the parent
// read its height. The HTML is sanitised all the same.
function DigestFrame({ html }) {
  const [height, setHeight] = useState(480);
  const doc = `<!doctype html><html><head><meta charset="utf-8"><base target="_blank"></head><body style="margin:0">${html}</body></html>`;
  return (
    <iframe
      title="Aperçu du récap"
      className="hd-digest-frame"
      sandbox="allow-same-origin"
      srcDoc={doc}
      style={{ height }}
      onLoad={(e) => {
        const body = e.currentTarget.contentDocument?.body;
        if (body) setHeight(Math.min(900, body.scrollHeight + 4));
      }}
    />
  );
}

// Re-run the Google OAuth flow for the current account.
//
// Nothing in the app offered this, yet the digest card told people to "reconnect
// Gmail" when nothing arrived: the only advice given for an expired or
// insufficient authorisation pointed at a button that did not exist. Same call
// as the login screen (authService.getAuthUrl -> data.authUrl), because it is
// the same flow: the callback route re-issues the session.
function GmailAccount() {
  const toast = useToast();
  const confirm = useConfirm();
  const [redirecting, setRedirecting] = useState(false);
  const email = localStorage.getItem('userEmail') || '';

  const reconnect = async () => {
    // Google reuses whichever account is active in the browser. Signing in with
    // another address would quietly land the user in a different Mailsorter
    // account, so the address to pick is spelled out before leaving the app.
    if (
      !(await confirm({
        title: 'Reconnecter Gmail ?',
        message: email
          ? `Vous allez être redirigé vers Google pour réautoriser Mailsorter. Choisissez bien ${email} : un autre compte vous connecterait ailleurs.`
          : 'Vous allez être redirigé vers Google pour réautoriser Mailsorter.',
        confirmLabel: 'Continuer vers Google',
        cancelLabel: 'Rester ici',
      }))
    ) {
      return;
    }

    setRedirecting(true);
    try {
      const response = await authService.getAuthUrl({ reconnect: true });
      const authUrl = response.data?.authUrl;
      // A 200 without a URL would otherwise leave the button spinning forever.
      if (!authUrl) throw new Error('authUrl manquant');
      track('gmail_reconnect');
      window.location.href = authUrl;
    } catch (err) {
      toast.error(errText(err, 'Impossible de démarrer la reconnexion. Réessayez.'));
      setRedirecting(false);
    }
  };

  return (
    <div className="card animate-fade-up mt-6 p-7">
      <div className="mb-1 flex items-center gap-2">
        <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-brand-50">
          <Google size={18} />
        </span>
        <h2 className="text-lg font-bold text-ink-900">Compte Gmail</h2>
      </div>
      <p className="mb-5 text-sm text-muted">
        Rejouez l'autorisation Google si le digest n'arrive plus, si le tri échoue soudainement, ou si vous avez
        révoqué l'accès depuis votre compte Google.{' '}
        <span className="font-semibold text-ink-700">Vos règles, protections et historique sont conservés.</span>
      </p>

      <div className="flex flex-wrap items-center gap-3">
        <button onClick={reconnect} disabled={redirecting} className="btn-secondary">
          {redirecting ? <Spinner size={16} /> : <Refresh size={16} />} Reconnecter Gmail
        </button>
        {email && (
          <span className="text-sm text-muted">
            Compte connecté : <span className="font-mono text-xs text-ink-700">{email}</span>
          </span>
        )}
      </div>
    </div>
  );
}

// Manage the protected-senders list: addresses or whole domains that no
// automated pass (AI, rules, auto-pilot, bulk) may ever archive, trash or
// delete. A safety net for your VIPs.
function ProtectedSenders() {
  const toast = useToast();
  const [items, setItems] = useState([]);
  const [value, setValue] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await protectService.list();
      setItems(data.protected || []);
    } catch (err) {
      // A failed load must never read as "aucun expéditeur protégé": on this
      // card that sentence claims the safety net is gone, which would push the
      // user to re-add VIPs that are in fact still protected.
      setError(errText(err, "La liste n'a pas pu être chargée."));
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleAdd = async (e) => {
    e.preventDefault();
    const v = value.trim();
    if (!v) return;
    setAdding(true);
    try {
      const { data } = await protectService.add(v);
      setItems((prev) => [data, ...prev.filter((i) => i.value !== data.value)]);
      setValue('');
      // A successful add also proves the list is reachable again.
      setError(null);
      toast.success(`${data.value} protégé`);
    } catch (err) {
      toast.error(errText(err, 'Ajout impossible.'));
    } finally {
      setAdding(false);
    }
  };

  // Optimistic on purpose: removing a chip is cheap to undo (re-add the same
  // address) and the failure path puts the chip back with a toast.
  const handleRemove = async (item) => {
    setItems((prev) => prev.filter((i) => i.id !== item.id));
    try {
      await protectService.remove(item.id);
    } catch (err) {
      toast.error(errText(err, 'Suppression impossible.'));
      load();
    }
  };

  return (
    <div className="card animate-fade-up mt-6 p-7">
      <div className="mb-1 flex items-center gap-2">
        <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-positive-50 text-positive-600">
          <Shield size={18} />
        </span>
        <h2 className="text-lg font-bold text-ink-900">Expéditeurs protégés</h2>
      </div>
      <p className="mb-5 text-sm text-muted">
        Leurs emails ne seront <span className="font-semibold text-ink-700">jamais archivés ni supprimés automatiquement</span>,
        ni par l'IA, ni par les règles, ni en masse. Ajoutez une adresse (<span className="font-mono text-xs">boss@corp.com</span>)
        ou un domaine entier (<span className="font-mono text-xs">corp.com</span>).
      </p>

      <form onSubmit={handleAdd} className="mb-5 flex gap-2">
        <input
          value={value}
          onChange={(e) => setValue(e.target.value)}
          className="input flex-1"
          placeholder="adresse@exemple.com ou exemple.com"
          aria-label="Adresse ou domaine à protéger"
        />
        <button type="submit" disabled={adding} className="btn-primary shrink-0">
          {adding ? <Spinner size={18} /> : <Shield size={16} />} Protéger
        </button>
      </form>

      {loading ? (
        <div className="flex justify-center py-6">
          <Spinner size={22} className="text-brand-500" />
        </div>
      ) : error ? (
        <ErrorState compact title="Liste indisponible" message={error} onRetry={load} />
      ) : items.length === 0 ? (
        <EmptyState
          compact
          tone="positive"
          Icon={Shield}
          title="Aucun expéditeur protégé"
          description="Ajoutez vos VIP ci-dessus : leurs emails resteront hors de portée de tout tri automatique."
        />
      ) : (
        <div className="flex flex-wrap gap-2">
          {items.map((item) => (
            <span
              key={item.id || item.value}
              className="chip group bg-positive-50 text-positive-700"
              title={item.kind === 'domain' ? 'Domaine entier' : 'Adresse'}
            >
              <Shield size={13} />
              <span className="font-mono text-xs">{item.value}</span>
              <button
                onClick={() => handleRemove(item)}
                className="ml-0.5 rounded-full p-0.5 text-positive-600 transition-colors hover:bg-positive-100 hover:text-positive-700"
                aria-label={`Retirer ${item.value} des expéditeurs protégés`}
              >
                <X size={13} />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// Settings holds only per-user preferences.
//
// Two things used to live here and no longer do. The Gmail API credentials are
// a single instance-wide OAuth app shared by every account: editing them from a
// user-facing screen let anyone reconfigure the login flow for everyone, so
// they moved to the deployment environment. Data export and account deletion
// are account-level actions rather than preferences, so they moved to /account.
// Reconnecting Gmail stayed: it re-runs the OAuth flow for this user only and
// changes nothing for anyone else.

function AISettingsCard() {
  const { toast } = useToast();
  const confirm = useConfirm();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [providers, setProviders] = useState([]);
  const [currentSettings, setCurrentSettings] = useState(null);

  const [selectedProvider, setSelectedProvider] = useState('mistral');
  const [apiKey, setApiKey] = useState('');
  const [showApiKey, setShowApiKey] = useState(false);
  const [model, setModel] = useState('');
  const [customModel, setCustomModel] = useState('');
  const [baseUrl, setBaseUrl] = useState('');

  const loadAI = useCallback(async () => {
    try {
      setLoading(true);
      const [provRes, setRes] = await Promise.all([
        aiService.getProviders(),
        aiService.getSettings(),
      ]);
      const provList = provRes.data || [];
      setProviders(provList);
      const s = setRes.data || {};
      setCurrentSettings(s);

      if (s.configured && s.provider) {
        setSelectedProvider(s.provider);
        const match = provList.find((p) => p.name === s.provider);
        if (match && match.models && match.models.includes(s.model)) {
          setModel(s.model);
          setCustomModel('');
        } else if (s.model) {
          setModel('custom');
          setCustomModel(s.model);
        } else {
          setModel(match?.defaultModel || '');
        }
        setBaseUrl(s.baseUrl || '');
      } else {
        setSelectedProvider(s.provider || 'mistral');
        const match = provList.find((p) => p.name === (s.provider || 'mistral'));
        setModel(match?.defaultModel || '');
      }
    } catch (err) {
      console.error('Failed to load AI settings:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAI();
  }, [loadAI]);

  const activeProvider = useMemo(() => {
    return (
      providers.find((p) => p.name === selectedProvider) || {
        name: selectedProvider,
        displayName: selectedProvider,
        requiresApiKey: true,
        requiresUrl: false,
        defaultModel: '',
        models: [],
      }
    );
  }, [providers, selectedProvider]);

  const handleSelectProvider = (name) => {
    setSelectedProvider(name);
    setTestResult(null);
    const info = providers.find((p) => p.name === name);
    if (!info) return;

    if (currentSettings?.configured && currentSettings.provider === name) {
      if (info.models?.includes(currentSettings.model)) {
        setModel(currentSettings.model);
        setCustomModel('');
      } else if (currentSettings.model) {
        setModel('custom');
        setCustomModel(currentSettings.model);
      } else {
        setModel(info.defaultModel || '');
      }
      setBaseUrl(currentSettings.baseUrl || '');
    } else {
      setModel(info.defaultModel || '');
      setCustomModel('');
      if (name === 'ollama') {
        setBaseUrl('http://localhost:11434');
      } else {
        setBaseUrl('');
      }
    }
  };

  const effectiveModel = model === 'custom' ? customModel.trim() : model;

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const payload = {
        provider: selectedProvider,
        apiKey: apiKey.trim(),
        model: effectiveModel,
        baseUrl: baseUrl.trim(),
      };
      await aiService.testSettings(payload);
      setTestResult({
        success: true,
        message: 'Connexion réussie ! Le modèle répond correctement.',
      });
      toast.success('Test réussi : votre IA est opérationnelle.');
    } catch (err) {
      const msg = errText(err, 'Échec du test de connexion.');
      setTestResult({ success: false, message: msg });
      toast.error(msg);
    } finally {
      setTesting(false);
    }
  };

  const handleSave = async (e) => {
    if (e) e.preventDefault();
    setSaving(true);
    setTestResult(null);
    try {
      const payload = {
        provider: selectedProvider,
        apiKey: apiKey.trim(),
        model: effectiveModel,
        baseUrl: baseUrl.trim(),
      };
      await aiService.updateSettings(payload);
      toast.success('Configuration IA enregistrée avec succès.');
      setApiKey('');
      await loadAI();
    } catch (err) {
      toast.error(errText(err, "Erreur lors de l'enregistrement."));
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    const ok = await confirm({
      title: "Rétablir l'IA de l'instance ?",
      message:
        "Votre clé et vos paramètres personnalisés seront effacés. Le tri automatique réutilisera le fournisseur IA par défaut du serveur.",
      confirmLabel: 'Rétablir',
      danger: true,
    });
    if (!ok) return;

    try {
      await aiService.deleteSettings();
      toast.success("Fournisseur d'IA de l'instance rétabli.");
      setApiKey('');
      setTestResult(null);
      await loadAI();
    } catch (err) {
      toast.error(errText(err, 'Erreur lors de la réinitialisation.'));
    }
  };

  if (loading) {
    return (
      <div className="card animate-fade-up mt-6 flex justify-center p-8">
        <Spinner size={24} className="text-brand-500" />
      </div>
    );
  }

  return (
    <div className="card animate-fade-up mt-6 p-7">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-indigo-50 text-indigo-600 dark:bg-indigo-950/40 dark:text-indigo-400">
            <Sparkles size={18} />
          </span>
          <div>
            <h2 className="font-display text-lg font-bold text-ink-900">Intelligence Artificielle & Fournisseurs</h2>
          </div>
        </div>

        {currentSettings?.configured ? (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
            Clé personnalisée active ({currentSettings.provider})
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-ink-100 px-3 py-1 text-xs font-medium text-muted border border-hairline">
            Fournisseur instance ({currentSettings?.provider || 'Défaut'})
          </span>
        )}
      </div>

      <p className="mb-5 text-sm text-muted">
        Configurez le modèle d'IA pour le tri et l'analyse de vos emails. Vous pouvez utiliser le fournisseur par défaut de l'instance ou apporter votre propre clé API (Bring Your Own Key).
      </p>

      {/* Provider selection */}
      <div className="mb-5">
        <label className="mb-2 block text-xs font-semibold uppercase tracking-wider text-muted">
          Sélectionnez un fournisseur
        </label>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
          {providers.map((p) => {
            const isSelected = selectedProvider === p.name;
            const isCurrent = currentSettings?.configured && currentSettings.provider === p.name;
            return (
              <button
                key={p.name}
                type="button"
                onClick={() => handleSelectProvider(p.name)}
                className={`flex flex-col items-start rounded-xl border p-3 text-left transition-all ${
                  isSelected
                    ? 'border-brand-500 bg-brand-50/50 shadow-sm dark:bg-brand-950/20'
                    : 'border-hairline hover:border-ink-300 hover:bg-ink-50/50'
                }`}
              >
                <div className="flex w-full items-center justify-between">
                  <span className="text-sm font-semibold text-ink-900">{p.displayName}</span>
                  {isSelected && <Check size={14} className="text-brand-600" />}
                </div>
                <span className="mt-1 text-xs text-muted">
                  {p.requiresApiKey ? 'Clé API requise' : 'Auto-hébergé / Local'}
                </span>
                {isCurrent && (
                  <span className="mt-1.5 inline-block rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] font-bold text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300">
                    Actif
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Config form */}
      <form onSubmit={handleSave} className="space-y-4 rounded-xl border border-hairline/80 bg-ink-50/40 p-4">
        {activeProvider.requiresApiKey && (
          <div>
            <div className="mb-1 flex items-center justify-between">
              <label className="text-xs font-semibold text-ink-900">
                Clé API {activeProvider.displayName}
              </label>
              {currentSettings?.configured && currentSettings.provider === selectedProvider && currentSettings.apiKeyMasked && (
                <span className="text-xs text-muted">
                  Clé enregistrée : <code className="font-mono">{currentSettings.apiKeyMasked}</code>
                </span>
              )}
            </div>
            <div className="relative">
              <input
                type={showApiKey ? 'text' : 'password'}
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder={
                  currentSettings?.configured && currentSettings.provider === selectedProvider
                    ? 'Laisser vide pour conserver la clé actuelle'
                    : 'sk-...'
                }
                className="input-field w-full pr-20 text-sm font-mono"
              />
              <button
                type="button"
                onClick={() => setShowApiKey(!showApiKey)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-xs font-medium text-muted hover:text-ink-900 px-2 py-1 rounded"
              >
                {showApiKey ? 'Masquer' : 'Afficher'}
              </button>
            </div>
            <p className="mt-1 text-[11px] text-muted">
              Votre clé est chiffrée avec AES-256-GCM avant stockage et n'est jamais exposée publiquement.
            </p>
          </div>
        )}

        {activeProvider.requiresUrl && (
          <div>
            <label className="mb-1 block text-xs font-semibold text-ink-900">
              URL du serveur
            </label>
            <input
              type="text"
              value={baseUrl}
              onChange={(e) => setBaseUrl(e.target.value)}
              placeholder={selectedProvider === 'ollama' ? 'http://localhost:11434' : 'https://api.groq.com/openai/v1/chat/completions'}
              className="input-field w-full text-sm font-mono"
            />
            <p className="mt-1 text-[11px] text-muted">
              {selectedProvider === 'ollama'
                ? "Adresse de votre instance Ollama locale ou distante."
                : "Point de terminaison compatible OpenAI (ex. Groq, Together AI, vLLM, LM Studio)."}
            </p>
          </div>
        )}

        <div>
          <label className="mb-1 block text-xs font-semibold text-ink-900">
            Modèle
          </label>
          <div className="flex gap-2">
            <select
              value={model}
              onChange={(e) => setModel(e.target.value)}
              className="input-field flex-1 text-sm"
            >
              {activeProvider.models?.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
              <option value="custom">Autre modèle personnalisé...</option>
            </select>
          </div>
          {model === 'custom' && (
            <div className="mt-2">
              <input
                type="text"
                value={customModel}
                onChange={(e) => setCustomModel(e.target.value)}
                placeholder="ex. llama-3.3-70b-versatile, claude-3-5-haiku-latest..."
                className="input-field w-full text-sm font-mono"
              />
            </div>
          )}
        </div>

        {testResult && (
          <div
            className={`rounded-lg p-3 text-xs flex items-start gap-2 ${
              testResult.success
                ? 'bg-emerald-50 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800'
                : 'bg-rose-50 text-rose-800 dark:bg-rose-950/40 dark:text-rose-300 border border-rose-200 dark:border-rose-800'
            }`}
          >
            {testResult.success ? <Check size={16} className="shrink-0 mt-0.5" /> : <Alert size={16} className="shrink-0 mt-0.5" />}
            <span>{testResult.message}</span>
          </div>
        )}

        <div className="flex flex-wrap items-center justify-between gap-2 pt-2 border-t border-hairline">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleTest}
              disabled={testing || saving || (activeProvider.requiresApiKey && !apiKey && !currentSettings?.apiKeyMasked)}
              className="btn btn-secondary text-xs"
            >
              {testing ? <Spinner size={14} className="mr-1.5" /> : null}
              Tester la connexion
            </button>
            <button
              type="submit"
              disabled={saving || testing}
              className="btn btn-primary text-xs"
            >
              {saving ? <Spinner size={14} className="mr-1.5" /> : null}
              Enregistrer
            </button>
          </div>

          {currentSettings?.configured && (
            <button
              type="button"
              onClick={handleReset}
              className="text-xs text-rose-600 hover:text-rose-700 hover:underline"
            >
              Rétablir l'IA de l'instance
            </button>
          )}
        </div>
      </form>
    </div>
  );
}

function Settings() {
  const hotel = useHotel();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // One GET for the whole screen. Each preference card used to call
  // getSettings() on mount, so opening this page fired the same request three
  // times, and each card failed (or not) on its own, showing defaults as if
  // they were the saved values.
  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await accountService.getSettings();
      setSettings(data || {});
    } catch (err) {
      setError(errText(err, "Vos réglages n'ont pas pu être chargés."));
      setSettings(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // The PUT answers with the full settings record: merging keeps the cards in
  // sync with the server without a second round-trip.
  const applySaved = useCallback((data) => {
    setSettings((prev) => ({ ...(prev || {}), ...(data || {}) }));
  }, []);

  return (
    <div className="mx-auto max-w-3xl px-4 py-10 sm:px-6">
      {hotel ? (
        <PageHead
          floor="/settings"
          title="Réglages"
          sub="Le tri en arrière-plan, le récap du matin, votre boîte et les expéditeurs qu'on ne touche pas."
        />
      ) : (
        <div className="mb-8 flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-brand-50 text-brand-600">
            <SettingsIcon size={22} />
          </span>
          <div>
            <h1 className="font-display text-2xl font-extrabold tracking-tight text-ink-900">Réglages</h1>
            <p className="text-sm text-muted">
              Synchronisation automatique, digest quotidien, compte Gmail et expéditeurs protégés.
            </p>
          </div>
        </div>
      )}

      {loading ? (
        <div className="card mt-6 flex justify-center p-10">
          <Spinner size={26} className="text-brand-500" />
        </div>
      ) : error ? (
        <div className="card mt-6">
          <ErrorState title="Réglages indisponibles" message={error} onRetry={load} />
        </div>
      ) : (
        settings && (
          <>
            <AutoSyncSettings settings={settings} onSaved={applySaved} />
            <DigestSettings settings={settings} onSaved={applySaved} />
          </>
        )
      )}

      <AISettingsCard />
      <GmailAccount />
      <MailboxAccount />
      <ProtectedSenders />
    </div>
  );
}

// MailboxAccount is the way in to /connect. It lives here, beside the Gmail
// account card, because it is the same question asked differently: how does
// Mailsorter reach your mail. A user of the hosted service has no other path,
// Google offering them none.
function MailboxAccount() {
  const [mailbox, setMailbox] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    mailboxService
      .get()
      .then(({ data }) => {
        if (!cancelled) setMailbox(data.mailbox || null);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="card animate-fade-up mt-6 p-7">
      <div className="mb-1 flex items-center gap-2">
        <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-brand-50">
          <Mail size={18} className="text-brand-600" />
        </span>
        <h2 className="text-lg font-bold text-ink-900">Boîte mail</h2>
      </div>
      <p className="mb-5 text-sm text-muted">
        Branchez une boîte en IMAP avec un mot de passe d'application. C'est le chemin des
        fournisseurs autres que Gmail, et le seul sur le service en ligne.
      </p>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-muted">
          <Spinner size={16} /> Lecture
        </div>
      ) : mailbox ? (
        <div className="rounded-xl border border-hairline/70 bg-ink-50/60 px-4 py-3">
          <p className="text-xs font-bold uppercase tracking-wider text-ink-500">Branchée</p>
          <p className="mt-1 break-all font-mono text-sm text-ink-800">{mailbox.username}</p>
        </div>
      ) : (
        <p className="text-sm text-muted">Aucune boîte branchée pour l'instant.</p>
      )}

      <Link to="/connect" className="btn-secondary mt-6">
        {mailbox ? 'Gérer la boîte' : 'Brancher une boîte'} <ChevronRight size={16} />
      </Link>
    </div>
  );
}

export default Settings;
